// src/constants/RESOURCE_CURATION_CONFIG.ts
import { assembleFamilyHistoryOcrRecord } from '@utils/ocr/familyHistoryOcrAssembler';
import { labResultsNormalizer } from '@utils/ocr/labResultsNormalizer';
import { assembleMedicationOcrRecord } from '@utils/ocr/medicationOcrAssembler';
import { assembleVitalSignOcrRecord } from '@utils/ocr/vitalSignsOcrAssembler';

/**
 * Aliases so both singular/plural / old keys still work.
 * This helps avoid breakage from older code paths.
 */
const FORM_TYPE_ALIASES: Record<string, string> = {
  // singular -> plural / UI keys
  Medication: 'Medications',
  Immunization: 'Immunizations',
  'Imaging Test': 'Imaging and Tests',
  'Emergency Visit': 'Emergency Visits',
  Hospitalization: 'Hospitalizations',
};

export const normalizeFormType = (formType: string): string => {
  if (formType in FORM_TYPE_ALIASES) {
    return FORM_TYPE_ALIASES[formType];
  }
  return formType;
};

type OcrMap = Record<string, string[]>;

type BooleanNormalizers = Record<string, (raw: string) => boolean>;

type ResourceCurationConfig = {
  /** Initial empty record for manual forms + base for auto-curation */
  defaults: any;
  /**
   * Map of target field -> array of OCR label keys to try, in priority order.
   *
   * The `location*` labels (`locationName`, `locationCity`, `locationRegion`,
   * `locationCountry`, `locationPostalCode`) are synthetic: they come from the
   * group's own `Location` resource rather than from this resource's own OCR
   * data, injected by `applyLocationFacility`. Listing one is how a form type
   * opts into the group facility; listing it last leaves any facility the
   * resource itself carries in front.
   */
  ocrMap?: OcrMap;
  /**
   * Fields whose OCR value may arrive as a FHIR structure rather than text, and
   * so must not be coerced to a string. Opt-in per field: `defaults` is merged
   * into the record before extraction, so any field with an object default would
   * otherwise look like structured OCR output.
   */
  structuredOcrFields?: string[];
  /** Optional per-field boolean converters (yes/true/1 → boolean) */
  booleanNormalizers?: BooleanNormalizers;
  /**
   * Optional hook to assemble complex shapes (e.g. components[], results[])
   * from the flat mapped values.
   */
  customOcrAssembler?: (
    flatMapped: Record<string, any>,
    bboxMap: Record<string, any[]>
  ) => any;
  /**
   * Optional hook to turn form values into final “model input”.
   *
   * Default behavior:
   *  - If the underlying record implements getModelInput(values),
   *    delegate to it (normal hydrated FHIR record).
   *  - Otherwise (plain auto-curation object), just return values.
   */
  modelInput?: (record: any, values: any) => any;
};

/**
 * Vital Sign and Body are one form and one shape. VitalSigns/FormDrilldown
 * renders both, switching on a TrackerType prop, and discern emits body codes
 * (body-height, body-weight, blood-sugar, body-mass-index) under the very same
 * `vital` resource title as the six true vital signs. So they share one config,
 * registered under both curation keys.
 */
const OBSERVATION_CURATION_CONFIG: ResourceCurationConfig = {
  defaults: {
    resourceType: 'Observation',
    status: 'final',
    category: [],
    codeFHIR: { text: '' },
    effectiveDateTime: null,
    valueQuantity: null,
    component: [],
    performer: null,
    encounter: null,
    specimen: null,
    referenceRange: null,
    interpretation: null,

    clinicalStatus: true,
    codeString: '',
    code: '',
    source: '',
    testDate: '',
    selfReported: false,
    components: [
      {
        code: '',
        display: '',
        value: null,
        unit: null,
        units: [],
      },
    ],
    associatedResources: [],
    _bboxMap: {},
  },
  ocrMap: {
    code: ['code'],
    display: ['display'],
    unit: ['unit'],
    value: ['value'],
    testDate: ['testDate'],
  },
  customOcrAssembler: (flat) => assembleVitalSignOcrRecord(flat),
};

/**
 * Curation config for all OCR-enabled resources.
 *
 * Keys are the same strings you use in:
 *  - RESOURCE_FORM_MAP
 *  - mapBBoxTitleToFormType
 *  - DynamicResourceForms
 */
const RAW_RESOURCE_CURATION_CONFIG: Record<string, ResourceCurationConfig> = {
  /* ------------------------- Surgeries (Procedure) ------------------------ */
  Surgeries: {
    defaults: {
      resourceType: 'Procedure',
      status: 'completed',
      code: { text: '' },
      performedDateTime: null,
      note: [],
      complication: [],
      followUp: '',

      anesthesia: '',
      anesthesiologist: '',
      city: '',
      complications: '',
      country: '',
      elb: '',
      facility: '',
      plan: '',
      postOperativeDiagnosis: '',
      practitioner: '',
      preoperativeDiagnosis: '',
      procedureDate: '',
      procedureNotes: '',
      visitReason: '',
      selfReported: false,
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      procedureName: ['title'],
      visitReason: ['visitReason'],
      preoperativeDiagnosis: ['preoperativeDiagnosis'],
      postOperativeDiagnosis: ['postOperativeDiagnosis'],
      complications: ['complications'],
      followUp: ['followUp'],
      procedureDate: ['procedureDate'],
      procedureNotes: ['procedureNotes'],
      practitioner: ['practitioner'],
      anesthesiologist: ['anesthesiologist'],
      plan: ['plan'],
      facility: ['facility', 'locationName'],
      city: ['locationCity'],
      country: ['locationCountry'],
    },
  },

  /* ---------------------- Medical Conditions (Condition) ------------------ */
  'Medical Conditions': {
    defaults: {
      resourceType: 'Condition',
      code: { text: '' },
      clinicalStatus: 'active',
      verificationStatus: { text: 'confirmed' },
      onsetDateTime: null,
      note: [],
      category: '',
      snomedCode: '',
      icd10Code: '',
      vitallCode: '',
      selfReported: false,
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      title: ['title', 'condition'],
      condition: ['title', 'condition'],
      onsetDateTime: ['onsetDateTime'],
    },
  },

  /* -------------------------- Allergies (AllergyIntolerance) ------------- */
  Allergies: {
    defaults: {
      resourceType: 'AllergyIntolerance',
      code: { text: '' },
      clinicalStatus: null,
      category: null,
      lastOccurrence: null,
      reaction: [],
      severity: '',
      reactionDescription: '',
      responseDescription: '',
      noAllergies: null,
      patient: { reference: '' },
      selfReported: false,
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      title: ['title'],
      severity: ['severity'],
      allergyType: ['allergyType'],
      lastKnownReaction: ['lastKnownReaction'],
      reactionDescription: ['reactionDescription'],
      responseDescription: ['responseDescription'],
      noAllergies: ['noAllergies'],
    },
    customOcrAssembler: (flat) => ({
      title: flat.title || '',
      substance: flat.title || '',
      severity: flat.severity || '',
      allergyType: flat.allergyType || '',
      lastKnownReaction: flat.lastKnownReaction || '',
      reactionDescription: flat.reactionDescription || '',
      responseDescription: flat.responseDescription || '',
      noAllergies: flat.noAllergies || '',
    }),
  },

  /* ---------------------------- Appointment ------------------------------- */
  Appointment: {
    defaults: {
      resourceType: 'Appointment',
      status: 'booked',
      reasonCode: null,
      start: null,
      end: null,
      comment: null,
      patientInstruction: null,
      locationResource: [],
      participant: [],

      title: '',
      appointmentNotes: '',
      diagnosis: '',
      facilityCity: '',
      facilityCountry: '',
      facilityName: '',
      facilityProvince: '',
      followUps: '',
      hcpName: '',
      selfReported: false,
      visitDate: '',
      showTime: true,
      serviceType: '',
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      diagnosis: ['diagnosis'],
      title: ['title'],
      facilityName: ['facilityName', 'locationName'],
      facilityCity: ['locationCity'],
      facilityProvince: ['locationRegion'],
      facilityCountry: ['locationCountry'],
      appointmentNotes: ['appointmentNotes'],
      followUps: ['followUps'],
      hcpName: ['hcpName'],
      visitDate: ['visitDate'],
      // The APPT_SERVICE_TYPE_MAP code, not its display: the autocomplete's
      // option values are that map's keys (DISC-288).
      serviceType: ['serviceType'],
    },
  },

  /* ------------------------ Emergency Visits (Encounter) ------------------ */
  'Emergency Visits': {
    defaults: {
      resourceType: 'Encounter',
      status: 'finished',
      class: { code: 'EMER', display: 'Emergency' },

      title: '',
      city: '',
      complications: '',
      country: '',
      diagnosis: '',
      endDate: '',
      erNotes: '',
      facility: '',
      followUp: '',
      practitioner: '',
      startDate: '',
      visitDurationDays: undefined,
      visitDurationUnits: '',
      selfReported: false,
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      title: ['title'],
      diagnosis: ['diagnosis'],
      followUp: ['followUp'],
      erNotes: ['erNotes'],
      practitioner: ['practitioner'],
      startDate: ['startDate'],
      endDate: ['endDate'],
      facility: ['facility', 'locationName'],
      city: ['locationCity'],
      country: ['locationCountry'],
    },
  },

  /* ------------------------ Hospitalizations (Encounter) ------------------ */
  Hospitalizations: {
    defaults: {
      resourceType: 'Encounter',
      status: 'finished',
      class: { code: 'IMP', display: 'Inpatient' },

      title: '',
      city: '',
      complications: '',
      country: '',
      diagnosis: '',
      endDate: '',
      erNotes: '',
      facility: '',
      followUp: '',
      practitioner: '',
      startDate: '',
      visitDurationDays: undefined,
      visitDurationUnits: '',
      selfReported: false,
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      title: ['title'],
      diagnosis: ['diagnosis'],
      followUp: ['followUp'],
      complications: ['complications'],
      practitioner: ['practitioner'],
      startDate: ['startDate'],
      endDate: ['endDate'],
      facility: ['facility', 'locationName'],
      city: ['locationCity'],
      country: ['locationCountry'],
    },
  },

  /* ---------------------------- Medications ------------------------------- */
  Medications: {
    defaults: {
      resourceType: 'MedicationStatement',
      status: 'active',
      medicationCodeableConcept: { text: '' },
      subject: { reference: '' },
      effectiveDateTime: null,
      effectivePeriod: null,
      dosage: [],
      category: null,
      extension: [],

      title: '',
      acquisitionType: 'prescription',
      dose: '',
      ingredients: [],
      // Seeded like `ingredients` above: the form indexes step 0 directly, and
      // the assembler only emits the key when OCR found dosage content.
      dosageSteps: [],
      doseForm: '',
      endingDate: '',
      frequency: '',
      instructions: '',
      route: '',
      startingDate: '',
      selfReported: false,
      associatedResources: [],
      isPatient: false,
      _bboxMap: {},
    },
    // Discern will eventually return FHIR directly, which makes most of this
    // redundant. Until then these are the labels it emits; any of them may be
    // absent. assembleMedicationOcrRecord decides what each one maps onto.
    ocrMap: {
      title: ['title', 'medication_text'],
      medication: ['medication', 'title', 'medication_text'],
      startingDate: ['startingDate', 'date'],
      route: ['route', 'medication_route'],
      frequency: ['frequency', 'frequency_text'],
      instructions: ['instructions', 'patient_instruction'],
      dosageText: ['dosage_text'],
      // Product
      ingredients: ['ingredients'],
      strengthText: ['strength_text'],
      doseForm: ['dose_form'],
      // Dose
      doseValue: ['dose_value'],
      doseUnit: ['dose_unit'],
      doseLow: ['dose_low'],
      doseHigh: ['dose_high'],
      doseRangeUnit: ['dose_range_unit'],
      // Schedule
      timingFrequency: ['timing_frequency'],
      timingPeriod: ['timing_period'],
      timingPeriodMax: ['timing_period_max'],
      timingPeriodUnit: ['timing_period_unit'],
      timingWhen: ['timing_when'],
      timingCount: ['timing_count'],
      asNeeded: ['as_needed'],
      durationValue: ['duration_value'],
      durationUnit: ['duration_unit'],
      // Ceiling
      maxDoseValue: ['max_dose_value'],
      maxDoseUnit: ['max_dose_unit'],
      maxDosePeriod: ['max_dose_period'],
      maxDosePeriodUnit: ['max_dose_period_unit'],
      // No form field holds an infusion rate; captured as free text.
      rateValue: ['rate_value'],
      rateUnit: ['rate_unit'],
      ratePeriod: ['rate_period'],
      ratePeriodUnit: ['rate_period_unit'],
    },
    // `ingredients` arrives as a FHIR Medication.ingredient[]. `timingWhen`
    // arrives as an array of EventTiming codes (["MORN"]) rather than text, so
    // it needs the same non-string-preserving path (VEG-804).
    structuredOcrFields: ['ingredients', 'timingWhen'],
    customOcrAssembler: (flat) => assembleMedicationOcrRecord(flat),
  },

  /* -------------------------- Family History ------------------------------ */
  'Family History': {
    defaults: {
      resourceType: 'FamilyMemberHistory',
      status: 'completed',
      relationship: {},
      condition: [],
      note: null,
      deceasedBoolean: false,
      bornDate: null,
      deceasedDate: null,
      extension: [],

      dateOfBirth: '',
      description: '',
      isDeceased: false,
      maternalPaternal: '',
      relationshipCode: '',
      relationshipName: '',
      conditions: [],
      selfReported: false,
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      relationshipName: ['relationshipName'],
      relationshipCode: ['relationshipCode'],
      maternalPaternal: ['maternalPaternal'],
      description: ['description'],
      isDeceased: ['isDeceased'],
      deceasedDate: ['deceasedDate'],
      conditions: ['conditions'],
      // Discern's per-condition age at onset. Needed as its own entry so the
      // assembler can see it: `conditions` arrives as a nested array, which
      // buildFlatAndMap drops, leaving these flat labels as the only route to
      // the condition rows.
      ageDiagnosed: ['ageDiagnosed'],
    },
    structuredOcrFields: ['conditions'],
    booleanNormalizers: {
      isDeceased: (raw) => {
        const v = (raw || '').toString().toLowerCase();
        return v === 'yes' || v === 'true' || v === '1';
      },
    },
    customOcrAssembler: (flat) => assembleFamilyHistoryOcrRecord(flat),
  },

  /* -------------------------- Immunizations ------------------------------- */
  Immunizations: {
    defaults: {
      resourceType: 'Immunization',
      status: 'not-done',
      vaccineCode: { text: '' },
      occurrenceDateTime: null,
      occurrenceString: null,
      expirationDateFHIR: null,
      lotNumberFHIR: '',
      routeFHIR: null,
      manufacturer: null,
      performer: null,
      protocolApplied: null,
      reasonCode: null,
      locationResource: null,
      extension: [],

      title: '',
      brand: '',
      city: '',
      country: '',
      dose: '',
      expirationDate: '',
      facility: '',
      immunizationDate: '',
      lotNumber: '',
      practitioner: '',
      region: '',
      route: '',
      selfReported: false,
      standard: true,
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      title: ['title'],
      immunizationDate: ['immunizationDate'],
      dose: ['dose'],
      practitioner: ['practitioner'],
      brand: ['brand'],
      route: ['route'],
      lotNumber: ['lotNumber'],
      facility: ['facility', 'locationName'],
      city: ['locationCity'],
      region: ['locationRegion'],
      country: ['locationCountry'],
    },
    customOcrAssembler: (flat) => ({
      title: flat.title || '',
      vaccineName: flat.title || '',
      immunizationDate: flat.immunizationDate || '',
      dose: flat.dose || '',
      practitioner: flat.practitioner || '',
      brand: flat.brand || '',
      route: flat.route || '',
      lotNumber: flat.lotNumber || '',
      facility: flat.facility || '',
      city: flat.city || '',
      region: flat.region || '',
      country: flat.country || '',
    }),
  },

  /* -------------------------- Imaging and Tests --------------------------- */
  'Imaging and Tests': {
    defaults: {
      resourceType: 'DiagnosticReport',
      status: 'final',
      code: { text: '' },
      effectiveDateTime: null,
      issued: null,
      conclusion: null,
      result: null,
      performer: null,
      encounter: null,
      imagingStudy: null,
      specimen: null,
      presentedForm: null,
      category: null,
      extension: [],

      title: '',
      city: '',
      codeString: '',
      country: '',
      details: '',
      facilityName: '',
      findings: '',
      history: '',
      imagesAvailable: true,
      practitioner: '',
      recommendations: '',
      reportedDate: '',
      selfReported: false,
      technique: '',
      testDate: '',
      typeName: '',
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      title: ['title'],
      code: ['code'],
      details: ['details'],
      testDate: ['testDate'],
      reportedDate: ['reportedDate'],
      history: ['history'],
      technique: ['technique'],
      findings: ['findings'],
      practitioner: ['practitioner'],
      recommendations: ['recommendations'],
      facilityName: ['facilityName', 'locationName'],
      city: ['locationCity'],
      country: ['locationCountry'],
    },
    customOcrAssembler: (flat) => ({
      title: flat.title || '',
      testName: flat.title || '',
      code: flat.code || '',
      details: flat.details || '',
      testDate: flat.testDate || '',
      reportedDate: flat.reportedDate || '',
      history: flat.history || '',
      technique: flat.technique || '',
      findings: flat.findings || '',
      practitioner: flat.practitioner || '',
      recommendations: flat.recommendations || '',
      facilityName: flat.facilityName || '',
      city: flat.city || '',
      country: flat.country || '',
    }),
  },

  /* --------------------- Vital Signs / Body (Observation) ----------------- */
  'Vital Sign': OBSERVATION_CURATION_CONFIG,
  Body: OBSERVATION_CURATION_CONFIG,

  /* ---------------------------- Lab Results ------------------------------- */
  'Lab Results': {
    defaults: {
      resourceType: 'DiagnosticReport',
      status: 'final',
      code: '',
      category: [],
      performer: [],
      subject: null,
      conclusion: '',
      result: [],
      effectiveDateTime: null,

      title: '',
      practitioner: '',
      city: '',
      country: '',
      facilityName: '',
      testDate: '',
      reportedDate: '',
      details: '',
      findings: '',
      history: '',
      recommendations: '',
      technique: '',
      typeName: '',
      selfReported: false,
      results: [
        {
          id: '',
          category: '',
          code: '',
          displayName: '',
          testDate: '',
          unit: '',
          value: '',
        },
      ],
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      title: ['title'],
      typeName: ['typeName'],
      performer: ['performer'],
      code: ['code'],
      displayName: ['displayName', 'display', 'disply'],
      unit: ['unit'],
      value: ['value'],
      testDate: ['testDate'],
      facilityName: ['facilityName', 'locationName'],
      city: ['locationCity'],
      country: ['locationCountry'],
    },
    customOcrAssembler: (flat, bboxMap) => labResultsNormalizer(bboxMap, flat),
  },

  /* ------------------------- Genetic Information -------------------------- */
  'Genetic Information': {
    defaults: {
      resourceType: 'Observation',
      status: 'final',
      category: [],
      code: { text: '' },
      effectiveDateTime: null,
      valueString: '',

      title: '',
      testDate: '',
      selfReported: false,
      associatedResources: [],
      _bboxMap: {},
    },
    ocrMap: {
      title: ['title', 'condition'],
      code: ['code', 'category'],
      testDate: ['testDate'],
    },
  },
};

/**
 * Attach a generic modelInput handler to every config:
 *  - If record has getModelInput → delegate to it
 *  - Else → just return values (auto-curation plain objects)
 */
Object.keys(RAW_RESOURCE_CURATION_CONFIG).forEach((key) => {
  const cfg = RAW_RESOURCE_CURATION_CONFIG[key];
  if (!cfg.modelInput) {
    cfg.modelInput = (record: any, values: any) => {
      if (record && typeof record.getModelInput === 'function') {
        return record.getModelInput(values);
      }
      return values;
    };
  }
});

export const RESOURCE_CURATION_CONFIG = RAW_RESOURCE_CURATION_CONFIG;

export const OCR_ENABLED_FORM_TYPES = Object.keys(
  RESOURCE_CURATION_CONFIG
) as string[];
